import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Edit, Save } from '@mui/icons-material';
import { ChevronLeft } from 'lucide-react';
import Pagination from './components/Pagination';
import { useAuth } from '../../../AuthProvider';

export default function McpUsage({ returnToHome }) {
  const { getAccessToken } = useAuth();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editableRow, setEditableRow] = useState(null);
  const [editedData, setEditedData] = useState({});

  useEffect(() => {
    fetchMcpUsers();
  }, [page]);

  async function fetchMcpUsers() {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        pageSize: 50,
        search,
      });
      const response = await fetch(
        `${process.env.REACT_APP_NODE_URL}/admin/mcp-users?${params.toString()}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAccessToken()}`,
          },
        },
      );
      if (!response.ok) throw new Error('Failed to fetch MCP users');
      const data = await response.json();
      setUsers(data.users || []);
      setTotalPages(data.totalPages || 1);
      setTotalUsers(data.totalUsers || 0);
    } catch (error) {
      console.error('Error fetching MCP users:', error);
    } finally {
      setLoading(false);
    }
  }

  function handleSearchSubmit(e) {
    e.preventDefault();
    setPage(1);
    fetchMcpUsers();
  }

  function handleEditClick(user) {
    setEditableRow(user._id);
    setEditedData({
      mcpToolCallsRemaining: user.mcpToolCallsRemaining ?? 0,
      mcpToolCallsTotal: user.mcpToolCallsTotal ?? 0,
      mcpToolCallsBase: user.mcpToolCallsBase ?? 0,
      mcpMessagesRemaining: user.mcpMessagesRemaining ?? 0,
      mcpMessagesTotal: user.mcpMessagesTotal ?? 0,
    });
  }

  async function handleSaveClick(userId) {
    const updated = users.map(u =>
      u._id === userId ? { ...u, ...editedData } : u,
    );
    setUsers(updated);
    setEditableRow(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODE_URL}/admin/update-user`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAccessToken()}`,
          },
          body: JSON.stringify({ userID: userId, ...editedData }),
        },
      );
      if (!response.ok) console.error('Error updating user');
    } catch (error) {
      console.error('Error updating user:', error);
    }
  }

  function handleInputChange(e, field) {
    setEditedData({ ...editedData, [field]: parseInt(e.target.value) || 0 });
  }

  function truncateKey(key) {
    if (!key) return '-';
    if (key.length <= 12) return key;
    return key.slice(0, 6) + '...' + key.slice(-4);
  }

  function formatDate(dateStr) {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  const tableCellStyle = {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
  };

  return (
    <div style={{ padding: '2rem', paddingBottom: '1rem' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Button
            onClick={returnToHome}
            sx={{
              minWidth: 'auto',
              padding: '0.5rem',
              color: '#39585c',
            }}
          >
            <ChevronLeft size={20} />
          </Button>
          <Typography
            sx={{
              color: '#39585c',
              fontSize: '1.5rem',
              fontWeight: '700',
            }}
          >
            MCP Usage
          </Typography>
          <Typography
            sx={{
              color: '#888',
              fontSize: '0.9rem',
            }}
          >
            {totalUsers} user{totalUsers !== 1 ? 's' : ''} with MCP access
          </Typography>
        </Box>
      </Box>

      <form
        onSubmit={handleSearchSubmit}
        style={{
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <TextField
          label='Search by name or email'
          size='small'
          style={{ marginRight: '10px', width: '300px' }}
          InputLabelProps={{ shrink: true }}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <Button
          variant='contained'
          type='submit'
          sx={{
            backgroundColor: '#39585c',
            '&:hover': { backgroundColor: '#39585c' },
          }}
        >
          Search
        </Button>
      </form>

      <TableContainer component={Paper}>
        <Table size='small'>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#39585c' }}>
              <TableCell sx={tableCellStyle}>Name</TableCell>
              <TableCell sx={tableCellStyle}>Email</TableCell>
              <TableCell sx={tableCellStyle}>Plan</TableCell>
              <TableCell sx={tableCellStyle}>API Key</TableCell>
              <TableCell sx={tableCellStyle}>Tool Calls Used</TableCell>
              <TableCell sx={tableCellStyle}>Tool Calls Remaining</TableCell>
              <TableCell sx={tableCellStyle}>Tool Calls Total</TableCell>
              <TableCell sx={tableCellStyle}>Monthly Reset Amount</TableCell>
              <TableCell sx={tableCellStyle}>Messages Used</TableCell>
              <TableCell sx={tableCellStyle}>Messages Remaining</TableCell>
              <TableCell sx={tableCellStyle}>Messages Total</TableCell>
              <TableCell sx={tableCellStyle}>Reset Date</TableCell>
              <TableCell sx={tableCellStyle}>Edit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 6 }, (_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 13 }, (_, j) => (
                    <TableCell key={j}>
                      <Skeleton />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13} align='center'>
                  No MCP users found
                </TableCell>
              </TableRow>
            ) : (
              users.map(user => {
                const toolCallsUsed =
                  (user.mcpToolCallsTotal ?? 0) -
                  (user.mcpToolCallsRemaining ?? 0);
                const messagesUsed =
                  (user.mcpMessagesTotal ?? 0) -
                  (user.mcpMessagesRemaining ?? 0);
                const isEditing = editableRow === user._id;

                return (
                  <TableRow key={user._id}>
                    <TableCell>
                      {user.firstname} {user.lastname}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.85rem' }}>
                      {user.email}
                    </TableCell>
                    <TableCell>{user.current_plan || '-'}</TableCell>
                    <TableCell
                      sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                      title={user.mcpApiKey || ''}
                    >
                      {truncateKey(user.mcpApiKey)}
                    </TableCell>
                    <TableCell>{toolCallsUsed}</TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          size='small'
                          type='number'
                          value={editedData.mcpToolCallsRemaining}
                          onChange={e =>
                            handleInputChange(e, 'mcpToolCallsRemaining')
                          }
                          sx={{ width: '80px' }}
                        />
                      ) : (
                        user.mcpToolCallsRemaining ?? 0
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          size='small'
                          type='number'
                          value={editedData.mcpToolCallsTotal}
                          onChange={e =>
                            handleInputChange(e, 'mcpToolCallsTotal')
                          }
                          sx={{ width: '80px' }}
                        />
                      ) : (
                        user.mcpToolCallsTotal ?? 0
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          size='small'
                          type='number'
                          value={editedData.mcpToolCallsBase}
                          onChange={e =>
                            handleInputChange(e, 'mcpToolCallsBase')
                          }
                          sx={{ width: '80px' }}
                        />
                      ) : (
                        user.mcpToolCallsBase ?? 0
                      )}
                    </TableCell>
                    <TableCell>{messagesUsed}</TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          size='small'
                          type='number'
                          value={editedData.mcpMessagesRemaining}
                          onChange={e =>
                            handleInputChange(e, 'mcpMessagesRemaining')
                          }
                          sx={{ width: '80px' }}
                        />
                      ) : (
                        user.mcpMessagesRemaining ?? 0
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          size='small'
                          type='number'
                          value={editedData.mcpMessagesTotal}
                          onChange={e =>
                            handleInputChange(e, 'mcpMessagesTotal')
                          }
                          sx={{ width: '80px' }}
                        />
                      ) : (
                        user.mcpMessagesTotal ?? 0
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                      {formatDate(user.mcpUsageResetDate)}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Save
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSaveClick(user._id)}
                        />
                      ) : (
                        <Edit
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleEditClick(user)}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
